import Service, { service } from '@ember/service';
import { cached, tracked } from '@glimmer/tracking';
import Store from '@ember-data/store';
import UserSettingModel from 'donor-elf-web/models/user-setting';
import FundSettingModel from 'donor-elf-web/models/fund-setting';
import OrgSettingModel from 'donor-elf-web/models/org-setting';
import AnnouncementModel from 'donor-elf-web/models/announcement';
import MailchimpFundSyncSlimModel from 'donor-elf-web/models/mailchimp-fund-sync-slim';
import FundsService from './funds-service';
import ContactCustomPropertyUserSettingModel from 'donor-elf-web/models/contact-custom-property-user-setting';
import UserFundSettingModel, {
  type UserFundSettingType,
} from 'donor-elf-web/models/user-fund-setting';
import AllSettingModel from 'donor-elf-web/models/all-setting';
import { indexArrayByPropertyName } from 'donor-elf-web/lib/array-utils';

export default class Settings extends Service {
  @service declare fundsService: FundsService;
  @service declare store: Store;

  fundId?: string; // keeps track of the fundId that the settings are loaded for
  @tracked userSettings?: UserSettingModel;
  @tracked fundSettings?: FundSettingModel;
  @tracked orgSettings?: OrgSettingModel;
  @tracked announcement?: AnnouncementModel;
  @tracked mailchimpFundSyncSlim?: MailchimpFundSyncSlimModel;
  @tracked fundIsSyncedWithUserNewsletterAccount?: boolean;
  @tracked
  contactCustomPropertyUserSetting?: ContactCustomPropertyUserSettingModel;
  @tracked userFundSettings: UserFundSettingModel[] = [];

  get dateFormat() {
    return this.userSettings?.dateFormat || 'MM/DD/YY';
  }

  get dateFormat4Year() {
    return this.dateFormat.replace('YY', 'YYYY');
  }

  get dateFormatNoYear() {
    return this.dateFormat.replace('/YY', '');
  }

  get showUserLastLogin() {
    return this.userSettings?.showUserLastLogin || false;
  }

  get hasMailchimpFundSync() {
    return this.mailchimpFundSyncSlim != null;
  }

  @cached
  get userFundSettingsByType() {
    return indexArrayByPropertyName(this.userFundSettings, 'settingType') as {
      [settingType in UserFundSettingType]: UserFundSettingModel;
    };
  }

  // call this function after a new UserFundSetting has been created
  onUserFundSettingCreated(newUserFundSetting: UserFundSettingModel) {
    this.userFundSettings = [...this.userFundSettings, newUserFundSetting];
  }

  // set fundId to -1 if you don't want the fund settings (eg in the funds/all route)
  loadSettings(fundId: string) {
    if (fundId === this.fundId) {
      return Promise.resolve();
    }

    return this.store
      .findRecord('all-setting', fundId)
      .then((allSetting: AllSettingModel) => {
        this.fundId = fundId;
        this.userSettings = allSetting.userSettings;
        this.fundSettings = allSetting.fundSettings;
        this.orgSettings = allSetting.orgSettings;
        this.announcement = allSetting.announcement;
        this.mailchimpFundSyncSlim = allSetting.mailchimpFundSyncSlim;
        this.fundIsSyncedWithUserNewsletterAccount =
          allSetting.fundIsSyncedWithUserNewsletterAccount;
        this.fundsService.goal = allSetting.goal;
        this.contactCustomPropertyUserSetting =
          allSetting.contactCustomPropertyUserSetting;
        this.userFundSettings = allSetting.userFundSettings?.slice() || [];
      });
  }

  unload() {
    this.userSettings = undefined;
    this.fundSettings = undefined;
    this.orgSettings = undefined;
    this.announcement = undefined;
    this.mailchimpFundSyncSlim = undefined;
    this.fundIsSyncedWithUserNewsletterAccount = undefined;
    this.fundsService.goal = undefined;
    this.contactCustomPropertyUserSetting = undefined;
    this.userFundSettings = [];
  }
}
